import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { GestaoEpiDropdown } from "../../../../domain/models/gestao_epi_dropdown";
import {
  GestaoRecebimentoEpiForm,
  GestaoRecebimentoEpiSchema,
} from "../../../schemas/ControleRecebimentoEpi_lista/gestao_recebimento_epi";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import {  useEffect } from "react";
import { GestaoRecebimentoEpi } from "../../../../domain/models/gestao_recebimento_epi";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DefaultFormDownloadInput } from "../../../../../../common/components/default_form_download_input";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedGestaoRecebimentoEpi: GestaoRecebimentoEpi | null;
  gestaoEpiDropdown: GestaoEpiDropdown[];
  fornecedoresDropdown: FornecedorDropDown[];
  centroCustoDropdown: CentroCustoDropDown[];
  onClose: () => void;
  onSubmit: (form: GestaoRecebimentoEpiForm) => void;
  handleDownloadFile: (filename: string) => void;
  onDeleteFile: () => void;
};

export const GestaoRecebimentoEpiModal = ({
  open,
  saving,
  loading,
  selectedGestaoRecebimentoEpi,
  gestaoEpiDropdown,
  fornecedoresDropdown,
  centroCustoDropdown,
  onClose,
  onSubmit,
  handleDownloadFile,
  onDeleteFile,
}: Props) => {
  const hookForm = useForm<GestaoRecebimentoEpiForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GestaoRecebimentoEpiSchema),
  });
  const watchIdGestaoEpi = hookForm.watch("idGestaoEpi");
  const watchQuantidade = hookForm.watch("quantidade");
  const watchValorUnitario = hookForm.watch("ValorUnitario");

  useEffect(() => {
    if (watchIdGestaoEpi) {
      const epiSelecionado = gestaoEpiDropdown.find(
        (epi) => epi.idGestaoEpi === watchIdGestaoEpi,
      );

      if (epiSelecionado) {
        hookForm.setValue("especificacao", epiSelecionado.especificacao || "");
      }
    } else {
      hookForm.setValue("especificacao", "");
    }
  }, [watchIdGestaoEpi, gestaoEpiDropdown, hookForm]);

  useEffect(() => {
    if (open) {
      if (selectedGestaoRecebimentoEpi) {
        hookForm.setValue(
          "idGestaoEpi",
          selectedGestaoRecebimentoEpi.idGestaoEpi,
        );
        hookForm.setValue("ca", selectedGestaoRecebimentoEpi.ca ?? "");
        hookForm.setValue(
          "quantidade",
          selectedGestaoRecebimentoEpi.quantidade,
        );
        hookForm.setValue(
          "idFornecedor",
          selectedGestaoRecebimentoEpi.idFornecedor,
        );
        hookForm.setValue(
          "dataRecebimento",
          selectedGestaoRecebimentoEpi.dataRecebimento,
        );
        hookForm.setValue(
          "dataValidade",
          selectedGestaoRecebimentoEpi.dataValidade,
        );
        hookForm.setValue(
          "idCentroCusto",
          selectedGestaoRecebimentoEpi.idCentroCusto,
        );
        hookForm.setValue(
          "ValorUnitario",
          selectedGestaoRecebimentoEpi.valorUnitario,
        );
        hookForm.setValue(
          "ValorTotal",
          selectedGestaoRecebimentoEpi.valorTotal,
        );
        hookForm.setValue("marca", selectedGestaoRecebimentoEpi.marca);
        hookForm.setValue(
          "notaFiscal",
          selectedGestaoRecebimentoEpi.notaFiscal,
        );
      } else {
        hookForm.reset();
      }
    }
  }, [open, selectedGestaoRecebimentoEpi, hookForm]);

  useEffect(() => {
    const qtd = Number(watchQuantidade) || 0;
    const valorUnit = Number(watchValorUnitario) || 0;

    const total = qtd * valorUnit;

    hookForm.setValue("ValorTotal", total, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [watchQuantidade, watchValorUnitario, hookForm]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Recebimento de EPI"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}
    >
      <div className="w-full flex flex-col gap-3 my-4">
        <DefaultFormAutocomplete
          name="idGestaoEpi"
          label="Nome do EPI"
          required={true}
          options={gestaoEpiDropdown}
          getOptionKey={(x) => x.idGestaoEpi}
          getOptionLabel={(x) => x.nome}
          reactHookForm={hookForm}
        />

        <div className="flex flex-row gap-3 w-full">
          <div className="flex-1">
            <DefaultFormInput
              name="idGestaoEpi"
              label="Codigo"
              reactHookForm={hookForm}
              readOnly={true}
            />
          </div>
          <div className="flex-1">
            <DefaultFormInput
              name="especificacao"
              label="Tipo/Numeração"
              reactHookForm={hookForm}
              readOnly={true}
            />
          </div>
        </div>
        <DefaultFormAutocomplete
          name="idCentroCusto"
          label="Centro de Custo"
          required={true}
          options={centroCustoDropdown}
          getOptionKey={(x) => x.id ?? 0}
          getOptionLabel={(x) => x.title ?? ""}
          reactHookForm={hookForm}
        />
        <DefaultFormAutocomplete
          name="idFornecedor"
          label="Fornecedor"
          options={fornecedoresDropdown}
          getOptionKey={(x) => x.idFornecedor}
          getOptionLabel={(x) => x.nomeFantasia}
          reactHookForm={hookForm}
        />
        <DefaultFormInput
          name="ca"
          label="CA (Certificado de Aprovação)"
          reactHookForm={hookForm}
        />
        <DefaultFormInput name="marca" label="Marca" reactHookForm={hookForm} />
        
        <div className="flex flex-row gap-3 w-full">
          <div className="flex-1">
            <FormMaskedInput
              name="quantidade"
              label="Quantidade"
              required={true}
              mask="00000000"
              maskPlaceholder=""
              placeholderChar=""
              reactHookForm={hookForm}
            />
          </div>
          <div className="flex-1">
            <DefaultFormInput
              name="notaFiscal"
              label="Nº da nota fiscal"
              reactHookForm={hookForm}
            />
          </div>
        </div>

        <div className="flex flex-row gap-3 w-full">
          <div className="flex-1">
            <DefaultFormDatePickerInput
              name="dataRecebimento"
              label="Data do recebimento"
              reactHookForm={hookForm}
            />
          </div>
          <div className="flex-1">
            <DefaultFormDatePickerInput
              name="dataValidade"
              label="Data da validade"
              reactHookForm={hookForm}
            />
          </div>
        </div>

        <div className="flex flex-row gap-3 w-full">
          <div className="flex-1">
            <DefaultFormMoneyInput
              name="ValorUnitario"
              label="Valor Unitário"
              reactHookForm={hookForm}
            />
          </div>
          <div className="flex-1">
            <DefaultFormMoneyInput
              name="ValorTotal"
              label="Valor Total"
              readOnly={true}
              reactHookForm={hookForm}
            />
          </div>
        </div>

        <div className="flex flex-row gap-3 w-full">
          <div className="flex-1">
            <DefaultFormDownloadInput
              name="ArquivoNotaFiscal"
              nameLimparFlag="limparArquivoNotaFiscal"
              saving={saving}
              filename={selectedGestaoRecebimentoEpi?.nomeNotaFiscal ?? null}
              reactHookForm={hookForm}
              onDownloadFile={(filename) => handleDownloadFile(filename)}
              onDeleteFile={() => onDeleteFile()}
            />
          </div>
          <div className="flex-1"></div>
        </div>
        
      </div>
    </BaseFormControlledModal>
  );
};
